import { AiUser, AiUserSubscription, BillingCycle, IsoCountryCode } from '../../types'
import { ComposerTier, ComposerTierType, getUserComposerTier } from './ComposerTier'
import { ConverterTier, ConverterTierType, getUserConverterTier } from './ConverterTier'
import { CreatorTier, CreatorTierType, getUserCreatorTier } from './CreatorTier'
import { FreeTier, FreeTierType } from './FreeTier'
import { PriceIDMap } from './constants'

export * from './BaseTier'
export * from './ComposerTier'
export * from './ConverterTier'
export * from './CreatorTier'
export * from './FreeTier'
export * from './SharedStripeConfig'
export * from './constants'

export type TierType = FreeTierType | CreatorTierType | ConverterTierType | ComposerTierType
export type GetTierFunction<T> = (user: Pick<AiUser, 'createdAt' | 'countryCode'>) => T

export const getPriceIdMap = (isProd: boolean, billingCycle: BillingCycle) =>
  PriceIDMap[isProd ? 'prod' : 'dev'][billingCycle]

/**
 * IMPORTANT: this will only fetch the tier information for the base tiers, not for any legacy or special tiers.
 * Most of the time you should use getTierForUser instead of this function.
 */
const getTierBySubscriptionType = (
  subscriptionType: AiUserSubscription
): typeof FreeTier | typeof ComposerTier | typeof CreatorTier | typeof ConverterTier => {
  return (
    [FreeTier, ComposerTier, CreatorTier, ConverterTier].find(
      (item) => item.subscriptionType === subscriptionType
    ) || FreeTier
  )
}

export const getLevelForSubscription = (subscription: AiUserSubscription) => {
  const tier = getTierBySubscriptionType(subscription)
  return tier.subscriptionLevel
}

export interface GetTierForUserType {
  createdAt: string
  subscription: AiUserSubscription
  billingCycle: BillingCycle
  countryCode: IsoCountryCode | null
  [x: string | number | symbol]: unknown
}

export const getTierForUser = (user: GetTierForUserType): TierType => {
  if (user.subscription === AiUserSubscription.CONVERTER) {
    return getUserConverterTier(user)
  }

  if (user.subscription === AiUserSubscription.CREATOR) {
    return getUserCreatorTier(user)
  }

  if (user.subscription === AiUserSubscription.COMPOSER) {
    return getUserComposerTier(user)
  }

  return getTierBySubscriptionType(user.subscription)
}

export const getSubscriptionName = (subscription: AiUserSubscription) => {
  switch (subscription) {
    case AiUserSubscription.FREE:
      return 'Free'
    case AiUserSubscription.CONVERTER:
      return 'Converter'
    case AiUserSubscription.CREATOR:
      return 'Creator'
    case AiUserSubscription.COMPOSER:
      return 'Composer'
  }
}

/**
 * The date when we will migrate all users to the new model slots system.
 * Any models created before this date will still be accessible.
 */
export const modelSlotsMigrationCutOffDate = new Date('2023-11-12T15:12:01.418-06:00')
